import { useState, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import './CourseLanding.css'
import logo from '../../assets/images/storiesLogo.svg'
import heroBg from '../../assets/images/lending/Rectangle 3.png'
import navFounder from '../../assets/images/lending/Group 2765.png'
import navMission from '../../assets/images/lending/Group 2766.png'
import navAudience from '../../assets/images/lending/Group 2767.png'
import navResults from '../../assets/images/lending/Group 2768.png'
import navProgram from '../../assets/images/lending/Group 2769.png'
import navPricing from '../../assets/images/lending/Group 2770.png'
import navFormat from '../../assets/images/lending/Group 2771.png'
import navFaq from '../../assets/images/lending/Group 2772.png'
interface CourseLandingProps {
  buyLink: string
  onBuyClick: (link: string) => void
}

const NAV_ITEMS = [
  { label: 'О создателе', id: 'founder', image: navFounder },
  { label: 'Миссия', id: 'mission', image: navMission },
  { label: 'Для кого', id: 'audience', image: navAudience },
  { label: 'Результаты', id: 'results', image: navResults },
  { label: 'Программа\nкурса', id: 'program', image: navProgram },
  { label: 'Тарифы', id: 'pricing', image: navPricing },
  { label: 'Формат\nобучения', id: 'format', image: navFormat },
  { label: 'FQA', id: 'faq', image: navFaq },
]

const DECORS = [
  { title: 'Торт «Нежные банты»' },
  { title: 'Торт «Бусы»' },
  { title: 'Торт «Нежность»' },
  { title: 'Торт «Корейский дизайн»' },
  { title: 'Торт «????»' },
  { title: 'Торт «Итальянский пирог»' },
  { title: 'Торт «Длинный»' },
  { title: 'Торт «Оригами»' },
  { title: 'Торт «Капуста»' },
  { title: 'Торт «Шторы»' },
]

const AUDIENCE_CARDS = [
  {
    title: 'Боитесь,\nчто торт развалится',
    description: 'не доедет или подведёт в самый ответственный момент',
    variant: 'light' as const,
  },
  {
    title: 'Не уверены в расчётах\nи пропорциях',
    description:
      'боитесь, что торт не влезет в холодильник или не выдержит собственный вес',
    variant: 'dark' as const,
  },
  {
    title: 'Сталкиваетесь\nс проблемами',
    description: 'трещины на мастике, перекошенные ярусы, «поплывший» крем',
    variant: 'light' as const,
  },
  {
    title: 'Хотите красивые свадебные дизайны',
    description:
      'но боитесь за декор или не знаете с какой стороны к нему подойти',
    variant: 'dark' as const,
  },
  {
    title: 'Упёрлись в потолок',
    description: 'не понимаете, как выйти на новый уровень качества',
    variant: 'light' as const,
  },
]

const MODULES = [
  {
    number: 1,
    title: 'Вводный модуль',
    description: 'Инвентарь, сборка начинок, расчёт пропорций',
    variant: 'dark' as const,
  },
  {
    number: 2,
    title: 'Начинки\nтортов',
    description: '8 уникальных и стабильных начинок от Pauline Cake',
    variant: 'light' as const,
  },
  {
    number: 3,
    title: 'Покрытия\nтортов',
    description: 'Ганаш, мастика, велюр: нанесение без дефектов',
    variant: 'dark' as const,
  },
  {
    number: 4,
    title: 'Сборка\nи конструкция',
    description: 'Сборка фальш-ярусов, живых тортов и гибридов',
    variant: 'dark' as const,
  },
  {
    number: 5,
    title: 'Свадебные торты. Декор',
    description: '10 свадебных дизайнов\nс пошаговой сборкой',
    variant: 'light' as const,
  },
  {
    number: 6,
    title: 'Упаковка\nи доставка',
    description: 'Тайминги, замеры, упаковка, действия в нештатных ситуациях',
    variant: 'dark' as const,
  },
]

const FORMAT_CARDS = [
  {
    title: 'Уроки в записи',
    description: 'Смотрите в любое удобное время',
  },
  {
    title: 'Постоянный доступ',
    description: 'Курс остаётся у вас навсегда',
  },
  {
    title: 'Поддержка куратора',
    description: 'Помощь, проверка и поддержка',
  },
]

const FAQ_ITEMS = [
  { question: 'Курс подойдёт новичкам или только опытным кондитерам?' },
  { question: 'А если у меня домашняя кухня и обычный холодильник?' },
  { question: 'Какое оборудование и инструменты понадобятся?' },
  { question: 'Как долго будет доступ к материалам?' },
  { question: 'Входят ли в курс рецепты начинок?' },
  { question: 'Можно ли оплатить из другой страны?' },
]

const CourseLanding = ({ buyLink, onBuyClick }: CourseLandingProps) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  const scrollToSection = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  return (
    <div className="cl-page">
      {/* === HERO === */}
      <section className="cl-hero">
        {/* Тёмный хедер-бар */}
        <div className="cl-hero-header">
          <img src={logo} alt="" className="cl-hero-logo" />
        </div>
        {/* Фото-блок с оверлеем */}
        <div
          className="cl-hero-photo"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="cl-hero-overlay">
            <div className="cl-hero-badge">Новая программа</div>
            <div className="cl-hero-title-block">
              <h1 className="cl-hero-title">СВАДЕБНЫЙ КУРС</h1>
              <p className="cl-hero-subtitle">от PAULINE CAKE</p>
            </div>
          </div>
          {/* Кнопка покупки — поверх нижней границы фото */}
          <button className="cl-hero-cta" onClick={() => onBuyClick(buyLink)}>
            КУПИТЬ КУРС
          </button>
        </div>
      </section>

      {/* === NAV === */}
      <section className="cl-nav">
        <div className="cl-nav-scroll">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className="cl-nav-item"
              onClick={() => scrollToSection(item.id)}
            >
              <img
                src={item.image}
                alt={item.label}
                className="cl-nav-circle"
              />

              <span className="cl-nav-label">{item.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* === WEDDING CAKE TITLE === */}
      <section className="cl-wedding-title">
        <h2 className="cl-wedding-heading">WEDDING CAKE</h2>
        <p className="cl-wedding-desc">
          Курс по многоярусным тортам от Pauline Cake{'\n'}
          Полная система от начинок до сборки многоярусных тортов и трендовых
          декоров
        </p>
      </section>

      {/* === FOUNDER === */}
      <section
        className="cl-founder"
        ref={el => {
          sectionRefs.current['founder'] = el
        }}
      >
        <div className="cl-founder-card">
          <div className="cl-founder-photo" />
          <div className="cl-section-label">Основатель</div>
          <div className="cl-founder-logo">
            <span className="cl-founder-logo-prefix">Основатель</span>
            <span className="cl-founder-logo-name">SECRET CHAT</span>
          </div>
          <p className="cl-founder-text">
            Привет, это Полина!
            {'\n\n'}Я кондитер с 12‑летним опытом, основатель Pauline Cake в
            Дубае, вы можете меня знать по популярным роликам из соцсетей.
            {'\n\n'}
            Один из моих лучших проектов, которым я безумно горжусь является
            SECRET CHAT — закрытое сообщество, где делюсь рецептами из нашего
            цеха, технологиями декора и стратегиями кондитерского бизнеса.
          </p>
        </div>
      </section>

      {/* === DECORS === */}
      <section
        className="cl-decors"
        ref={el => {
          sectionRefs.current['results'] = el
        }}
      >
        <h3 className="cl-section-title">Декоры на свадебном курсе</h3>
        <div className="cl-decors-scroll">
          {DECORS.map((decor, idx) => (
            <div key={idx} className="cl-decor-card">
              <div className="cl-decor-image" />
              <p className="cl-decor-name">{decor.title}</p>
            </div>
          ))}
        </div>
      </section>

      {/* === SOCIAL === */}
      <section className="cl-social">
        <h3 className="cl-social-title">Социальные сети</h3>
        <div className="cl-social-logo">
          <span>SECRET CHAT</span>
        </div>
        <div className="cl-social-icons">
          <div className="cl-social-icon" />
          <div className="cl-social-icon" />
          <div className="cl-social-icon" />
          <div className="cl-social-icon" />
        </div>
        <div className="cl-social-search">
          <input
            type="text"
            placeholder="Поиск"
            readOnly
            className="cl-social-input"
          />
        </div>
      </section>

      {/* === MISSION === */}
      <section
        className="cl-mission"
        ref={el => {
          sectionRefs.current['mission'] = el
        }}
      >
        <div className="cl-mission-card">
          <div className="cl-mission-photo" />
          <div className="cl-section-label">Миссия свадебного курса</div>
          <div className="cl-mission-logo">
            <span>SECRET CHAT</span>
          </div>
          <p className="cl-mission-text">
            Я знаю, что многим страшно начинать заниматься свадебным из-за
            особой ответственности.
            {'\n\n'}И моя задача - дать вам систему и погрузить вас во все
            процессы, чтобы вы сами почувствовали контроль над свадебными
            тортами.
            {'\n\n'}
            Вы будете точно знать, какую начинку выбрать, как рассчитать
            пропорции под любой холодильник, как собрать надёжные опоры,
            идеально покрыть торт и украсить так, чтобы декор не отвалился по
            дороге. Всё, что мы наработали за годы работы в Pauline Cake, — для
            вашего спокойного и успешного свадебного сезона.
          </p>
        </div>
      </section>

      {/* === AUDIENCE === */}
      <section
        className="cl-audience"
        ref={el => {
          sectionRefs.current['audience'] = el
        }}
      >
        <h2 className="cl-audience-course-name">СВАДЕБНЫЙ КУРС</h2>
        <h3 className="cl-section-title">подойдёт вам, если вы:</h3>
        <div className="cl-audience-scroll">
          {AUDIENCE_CARDS.map((card, idx) => (
            <div
              key={idx}
              className={`cl-audience-card cl-audience-card--${card.variant}`}
            >
              <div className="cl-audience-card-image" />
              <div className="cl-audience-card-body">
                <h4 className="cl-audience-card-title">{card.title}</h4>
                <p className="cl-audience-card-desc">{card.description}</p>
                <div className="cl-audience-card-btn">
                  <span>Решение</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === PROGRAM === */}
      <section
        className="cl-program"
        ref={el => {
          sectionRefs.current['program'] = el
        }}
      >
        <div className="cl-program-header">
          <span className="cl-program-label">Программа</span>
          <h2 className="cl-program-course-name">СВАДЕБНЫЙ КУРС</h2>
        </div>
        <div className="cl-program-scroll">
          {MODULES.map(mod => (
            <div
              key={mod.number}
              className={`cl-module-card cl-module-card--${mod.variant}`}
            >
              <div className="cl-module-badge">
                <span>Модуль {mod.number}</span>
              </div>
              <h4 className="cl-module-title">{mod.title}</h4>
              <p className="cl-module-desc">{mod.description}</p>
              <div className="cl-module-image" />
              <div className="cl-module-more">
                <span>Подробнее</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === PRICING === */}
      <section
        className="cl-pricing"
        ref={el => {
          sectionRefs.current['pricing'] = el
        }}
      >
        <h2 className="cl-pricing-label">Выбрать тариф</h2>
        <div className="cl-pricing-card">
          <h3 className="cl-pricing-title">Предзаказ</h3>
          <div className="cl-pricing-badge">ВЫГОДА 17%</div>
          <div className="cl-pricing-prices">
            <span className="cl-pricing-old">30 000 ₽</span>
            <span className="cl-pricing-strikethrough" />
            <span className="cl-pricing-new">25 000 ₽</span>
          </div>
          <p className="cl-pricing-note">
            Специальное предложение тарифа действует{'\n'}до 5 марта 2026 года
          </p>
        </div>
        <button className="cl-pricing-cta" onClick={() => onBuyClick(buyLink)}>
          КУПИТЬ КУРС
        </button>
      </section>

      {/* === FORMAT === */}
      <section
        className="cl-format"
        ref={el => {
          sectionRefs.current['format'] = el
        }}
      >
        <h2 className="cl-format-course-name">СВАДЕБНЫЙ КУРС</h2>
        <h3 className="cl-section-title">Формат обучения</h3>
        <div className="cl-format-scroll">
          {FORMAT_CARDS.map((card, idx) => (
            <div key={idx} className="cl-format-card">
              <div className="cl-format-card-image" />
              <div className="cl-format-card-body">
                <h4 className="cl-format-card-title">{card.title}</h4>
                <p className="cl-format-card-desc">{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === FAQ === */}
      <section
        className="cl-faq"
        ref={el => {
          sectionRefs.current['faq'] = el
        }}
      >
        <h2 className="cl-faq-label">FQA</h2>
        <h3 className="cl-section-title">Часто задаваемые вопросы</h3>
        <div className="cl-faq-list">
          {FAQ_ITEMS.map((item, idx) => (
            <div
              key={idx}
              className={`cl-faq-item ${openFaqIndex === idx ? 'cl-faq-item--open' : ''}`}
              onClick={() => toggleFaq(idx)}
            >
              <div className="cl-faq-question">
                <span>{item.question}</span>
                <ChevronDown size={16} className="cl-faq-chevron" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === FOOTER === */}
      <section className="cl-footer">
        <div className="cl-footer-social">
          <h3 className="cl-footer-social-title">Социальные сети</h3>
          <div className="cl-footer-logo">
            <span>SECRET CHAT</span>
          </div>
          <div className="cl-footer-icons">
            <div className="cl-social-icon cl-social-icon--light" />
            <div className="cl-social-icon cl-social-icon--light" />
            <div className="cl-social-icon cl-social-icon--light" />
            <div className="cl-social-icon cl-social-icon--light" />
          </div>
        </div>
        <div className="cl-footer-input-wrapper">
          <input
            type="text"
            placeholder="Поиск"
            readOnly
            className="cl-footer-input"
          />
        </div>
      </section>
    </div>
  )
}

export default CourseLanding
